
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMetricsDashboard } from '@/hooks/useMetricsDashboard';
import { useNorthStarMetrics } from '@/hooks/useNorthStarMetrics';
import NorthStarMetricsSection from './metrics/NorthStarMetricsSection';
import MetricCard from './metrics/MetricCard';
import TopPromptsTable from './metrics/TopPromptsTable';
import TimeFrameSelector from './metrics/TimeFrameSelector';
import PromptTimeFrameSelector from './metrics/PromptTimeFrameSelector';
import { 
  UserGrowthChart, 
  PromptInteractionsChart, 
  PromptCopiesChart, 
  PromptSavesChart, 
  PromptCollectionsChart, 
  RatingDistributionChart 
} from './metrics/MetricsCharts';

const AdminDashboard = () => {
  const {
    userTimeFrame,
    promptTimeFrame,
    userDateRange,
    promptDateRange,
    isLoading: metricsLoading,
    isRefreshing,
    userMetrics,
    promptMetrics,
    ratingMetrics,
    topPrompts,
    handleUserTimeFrameChange,
    handlePromptTimeFrameChange,
    setUserDateRange,
    setPromptDateRange,
    refreshData,
  } = useMetricsDashboard();

  const {
    metrics: northStarMetrics,
    isLoading: northStarLoading,
    refreshMetrics: refreshNorthStar
  } = useNorthStarMetrics();

  const handleRefreshAll = () => {
    refreshData();
    refreshNorthStar();
  };

  return (
    <div className="space-y-8">
      <Tabs defaultValue="north-star" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="north-star">North Star</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="north-star" className="space-y-6">
          <NorthStarMetricsSection 
            metrics={northStarMetrics} 
            isLoading={northStarLoading}
          />
        </TabsContent>
        
        <TabsContent value="analytics" className="space-y-6">
          {/* User Metrics Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">User Analytics</h3>
              <TimeFrameSelector
                timeFrame={userTimeFrame}
                onTimeFrameChange={handleUserTimeFrameChange}
                dateRange={userDateRange}
                onDateRangeChange={setUserDateRange}
              />
            </div>
            
            {userMetrics && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <MetricCard
                  title="Total Users"
                  value={userMetrics.totalUsers.value || 0}
                  metric={userMetrics.totalUsers}
                  icon="users"
                  isLoading={metricsLoading}
                />
                <MetricCard
                  title="New Users"
                  value={userMetrics.newUsers.value || 0}
                  metric={userMetrics.newUsers}
                  icon="trending-up"
                  isLoading={metricsLoading}
                />
                <MetricCard
                  title="User Growth"
                  value={`${userMetrics.totalUsers.percentage?.toFixed(1) || '0.0'}%`}
                  icon="calendar"
                  isLoading={metricsLoading}
                />
              </div>
            )}
            
            {/* User Growth Chart */}
            {userMetrics?.userGrowthData && (
              <div className="grid grid-cols-1 gap-6">
                <UserGrowthChart 
                  data={userMetrics.userGrowthData} 
                  isLoading={metricsLoading} 
                />
              </div>
            )}
          </div>

          {/* Prompt Metrics Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Prompt Analytics</h3>
              <PromptTimeFrameSelector
                timeFrame={promptTimeFrame}
                onTimeFrameChange={handlePromptTimeFrameChange}
                dateRange={promptDateRange}
                onDateRangeChange={setPromptDateRange}
              />
            </div>
            
            {promptMetrics && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <MetricCard
                  title="Total Prompt Copies"
                  value={promptMetrics.totalCopies.value || 0}
                  metric={promptMetrics.totalCopies}
                  icon="copy"
                  isLoading={metricsLoading}
                />
                <MetricCard
                  title="Total Saved Prompts"
                  value={promptMetrics.totalSaves.value || 0}
                  metric={promptMetrics.totalSaves}
                  icon="bookmark"
                  isLoading={metricsLoading}
                />
                <MetricCard
                  title="Collections Created"
                  value={promptMetrics.totalCollections.value || 0}
                  metric={promptMetrics.totalCollections}
                  icon="folder"
                  isLoading={metricsLoading}
                />
                <MetricCard
                  title="All-Time Copies"
                  value={promptMetrics.allTimeCopies || 0}
                  icon="trending-up"
                  isLoading={metricsLoading}
                />
              </div>
            )}
            
            {/* Prompt Interactions Chart */}
            {promptMetrics?.interactionData && (
              <div className="grid grid-cols-1 gap-6">
                <PromptInteractionsChart 
                  data={promptMetrics.interactionData} 
                  isLoading={metricsLoading} 
                />
              </div>
            )}
          </div>

          {/* Detailed Metrics Section */}
          {(promptMetrics?.copiesData || promptMetrics?.savesData || promptMetrics?.collectionsData) && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Detailed Metrics</h3>
              <p className="text-sm text-muted-foreground">Individual metric trends</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {promptMetrics?.copiesData && (
                  <PromptCopiesChart 
                    data={promptMetrics.copiesData} 
                    isLoading={metricsLoading} 
                  />
                )}
                {promptMetrics?.savesData && (
                  <PromptSavesChart 
                    data={promptMetrics.savesData} 
                    isLoading={metricsLoading} 
                  />
                )}
                {promptMetrics?.collectionsData && (
                  <PromptCollectionsChart 
                    data={promptMetrics.collectionsData} 
                    isLoading={metricsLoading} 
                  />
                )}
              </div>
            </div>
          )}

          {/* Rating Metrics Section */}
          {ratingMetrics && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Rating Analytics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <MetricCard
                  title="Total Reviews"
                  value={ratingMetrics.totalReviews || 0}
                  icon="star"
                  isLoading={metricsLoading}
                />
                <MetricCard
                  title="Average Rating"
                  value={ratingMetrics.averageRating.value?.toFixed(1) || '0.0'}
                  metric={ratingMetrics.averageRating}
                  icon="star"
                  isLoading={metricsLoading}
                />
                <MetricCard
                  title="Rating Distribution"
                  value={`${ratingMetrics.ratingDistribution?.length || 0} levels`}
                  icon="message-square"
                  isLoading={metricsLoading}
                />
              </div>
              
              {/* Rating Distribution Chart */}
              {ratingMetrics.ratingDistribution && ratingMetrics.ratingDistribution.length > 0 && (
                <div className="grid grid-cols-1 gap-6">
                  <RatingDistributionChart 
                    data={ratingMetrics.ratingDistribution} 
                    isLoading={metricsLoading} 
                  />
                </div>
              )}
            </div>
          )}

          {/* Top Prompts Section */}
          {topPrompts.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Top Performing Prompts</h3>
              <TopPromptsTable data={topPrompts} isLoading={metricsLoading} />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
